<template>
    <div class="dashboard">
        <template v-if="loading">
            <p>Loading your dashboard, please wait ...</p>
        </template>
        <template v-else>
            <div class="usage">
                <h2>
                    Your usage
                    <template v-if="credits">
                        <template v-if="credits.total > credits.base">({{ number(credits.used) }}/{{ number(credits.base) }} + {{ number(credits.total - credits.base) }} overage)</template>
                        <template v-else>({{ number(credits.used) }}/{{ number(credits.total) }})</template>
                    </template>
                </h2>
                <div class="progress-bar">
                    <div class="progress-bar-wrapper">
                        <div class="progress-wrap" :style="`width: ${percentage}%`">
                            <div class="progress-bar-loader">
                                <div class="progress-bar-loader-tooltip">{{ usage }}%</div>
                            </div>
                        </div>
                    </div>
                    <p>
                        You used <strong>{{ usage }}%</strong> of your plan.
                        <span v-if="extra" class="tooltip tooltip-left" :data-tooltip="`You used ${extra} more credits than your allocated plans credits (${account.plan.credits})`">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line>
                            </svg>
                        </span>
                    </p>
                </div>
            </div>

            <div class="upgrade white" v-if="showUpgrade()">
                <h3>Upgrade your plan</h3>
                <p>You used {{ usage }}% of your current plan.</p>
                <router-link :to="{ name: 'upgrade' }" title="Click here to upgrade your account" class="btn">Upgrade your account</router-link>
            </div>

            <div class="usage-chart white">
                <div class="row">
                    <h5 class="col-8">
                        <template v-if="statisticsLoaded">
                            <template v-if="filters.period === 'day'">You used {{ periodUsage }} credits in the last 24h</template>
                            <template v-else-if="filters.period === 'week'">You used {{ periodUsage }} credits in the last week</template>
                            <template v-if="filters.period === 'month'">You used {{ periodUsage }} credits in the last month</template>
                            <template v-if="filters.period === 'year'">You used {{ periodUsage }} credits in the past year</template>
                        </template>
                        <small>Your credits will reset in {{ renewInDays }} day{{ renewInDays|pluralize() }}.</small>
                    </h5>
                    <div class="col-4">
                        <div>
                            <div style="margin-bottom: 10px">
                                <select v-model="filters.period">
                                    <option value="day">Last 24 hours</option>
                                    <option value="week">Last 7 days</option>
                                    <option value="month">Last 30 days</option>
                                    <option value="year">Last 12 months</option>
                                </select>
                            </div>
                            <div>
                                <select v-model="filters.key" :disabled="filters.period == 'year'">
                                    <option :value="null">All API keys</option>
                                    <option v-for="key in apiKeys" :key="key.token" :value="key.token">{{ key.name || hideKey(key.token) }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                <apexchart v-if="statisticsLoaded" type="area" height="350" :options="chartOptions" :series="series"></apexchart>
                <p v-else>Loading your latest usage ...</p>
            </div>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import VueApexCharts from 'vue-apexcharts'
import Moment from 'moment'

export default {
    components: {
        apexchart: VueApexCharts
    },
    computed: {
        chartOptions () {
            const options = {
                chart: {
                    type: 'area',
                    height: 350,
                    animations: { enabled: true },
                    zoom: { enabled: false },
                    toolbar: { show: false }
                },
                colors: ['#6C20CD'],
                dataLabels: { enabled: false },
                stroke: { curve: 'smooth' },
                fill: {
                    type: 'gradient',
                    gradient: {
                        shadeIntensity: 1,
                        opacityFrom: 0.7,
                        opacityTo: 0.3
                    }
                },
                markers: {
                    size: 5,
                    hover: {
                        size: 9
                    }
                },
                tooltip: {
                    intersect: false,
                    shared: false,
                    followCursor: true,
                    x: { format: this.filters.period === 'day' ? 'dd MMM HH:00' : 'dd MMM' }
                },
                xaxis: { type: 'datetime' },
                animations: {
                    enabled: true,
                    easing: 'easeinout'
                }
            }

            if (this.filters.period !== 'year') {
                options.annotations = {
                    xaxis: [{
                        x: this.account.renew_date,
                        borderColor: '#6C20CD',
                        label: {
                            text: 'Renewal of your credits.',
                            orientation: 'horizontal',
                            borderWidth: 0,
                            borderRadius: 0,
                            style: {
                                color: '#fff',
                                background: '#6C20CD',
                                fontSize: '14px'
                            }
                        }
                    }]
                }
            } else {
                options.tooltip.x.format = "MMM 'yy"
            }

            return options
        },
        ...mapState({
            account: state => state
        })
    },
    watch: {
        filters: {
            handler () {
                if (this.filters.period === 'year') {
                    this.filters.key = null
                }

                this.loadStatistics()
            },
            deep: true
        }
    },
    data () {
        return {
            loading: true,
            statisticsLoaded: false,
            periodUsage: null,
            renewInDays: null,
            apiKeys: [],
            usage: null,
            credits: null,
            percentage: null,
            extra: false,
            series: [{ name: 'Credits', data: [] }],
            filters: {
                period: 'month',
                key: null
            }
        }
    },
    created () {
        this.$http.get('credits/usage').then(
            response => {
                // Convert a date in the format yyyy-mm-dd to a Moment object
                const renewDate = Moment(response.body.credits.renew_at, 'YYYY-MM-DD')
                // Get the difference in days between the current date and the renew date
                this.renewInDays = renewDate.diff(Moment(), 'days')
                if (this.renewInDays < 0) {
                    this.renewInDays = 0
                }
                this.credits = response.body.credits
                this.usage = Math.ceil((response.body.credits.used / response.body.credits.base) * 100)
                this.percentage = Math.min(this.usage, 100)
                if (this.usage > this.percentage) {
                    this.extra = response.body.credits.used - response.body.credits.base
                }
                this.loading = false
            }
        )

        this.loadStatistics()
        this.loadApiKeys()
    },
    methods: {
        async loadStatistics () {
            this.periodUsage = null
            this.statisticsLoaded = false
            this.series[0].data = []
            try {
                const response = await this.$http.get('credits/statistics', { params: this.filters })
                this.periodUsage = new Intl.NumberFormat('en-US').format(response.body.statistics.total || 0)

                for (const k in response.body.statistics.metrics) {
                    this.series[0].data.push({
                        x: k,
                        y: response.body.statistics.metrics[k]
                    })
                }

                this.statisticsLoaded = true
            } catch (e) {}
        },
        async loadApiKeys () {
            try {
                const keys = await this.$http.get('account/api/', { params: { limit: 100 } })
                this.apiKeys = keys.body.keys.data
            } catch (e) {}
        },
        showUpgrade () {
            if (this.usage < 15) {
                return false
            }

            if (!this.account.plan) {
                return true
            }

            return this.usage >= 85
        },
        number (num) {
            return num.toLocaleString()
        },
        hideKey (key) {
            return key.substr(0, 6) + '*'.repeat(key.length - 9) + key.substr(-3)
        }
    }
}
</script>

<style lang="scss" scoped>
    @import '../../public/css/vars';

    @keyframes progress-bar-animation {
        from { width: 0 }
        to { width: 100% }
    }

    .dashboard>div {
        margin: 80px 0 0 0;
        &:first-child { margin-top: 0 }
    }

    .usage p svg {
        width: 22px;
        height: 22px;
        stroke: $link-color-secondary;
        vertical-align: top;
    }

    .usage .progress {
        &-bar {
            height: 28px;
            background-color: #fff;
            border-radius: 20px;
            padding: 6px;
            margin-top: 40px;

            &>p {
                margin: 10px 0;
                text-align: right;
                color: $link-color-secondary
            }
        }
        &-bar-wrapper {
            background-color: #F5EEFC;
            width: 100%;
            height: 100%;
            border-radius: 20px;
        }

        &-wrap {
            height: 100%;
        }

        &-bar-loader {
            width: 100%;
            z-index: 10;
            border-radius: 20px;
            height: 100%;
            background: linear-gradient(89.47deg, #6C20CD -5.09%, #EA8A8A 115.63%);
            animation: progress-bar-animation 1s;
            position: relative;

            &-tooltip {
                position: absolute;
                background-color: #fff;
                font-weight: bold;
                padding: 5px;
                color: $menu-active-color;
                width: 60px;
                border-radius: 5px;
                left: calc(50% - 30px);
                top: -40px;
                box-shadow: $box-shadow;
                text-align: center;

                &:after {
                    content: "";
                    position: absolute;
                    top: 100%;
                    right: 50%;
                    transform: translate(50%, 0);
                    border-width: 5px;
                    border-style: solid;
                    border-color: #fff transparent transparent transparent;
                }
            }
        }
    }

    .upgrade {
        background: #fff url("/images/upgrade_rocket.svg") no-repeat top right;
        background-size: contain;

        p { color: $link-color-secondary }
        a.btn { display: inline-block; margin: 20px 0 0 }
    }

    .usage-chart { min-height: 500px }
</style>
